import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  Snackbar,
  Alert,
  Divider,
  Tab,
  Tabs,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import axios from '../../api/axios';
import ImageUploader from './ImageUploader';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const SettingsManager = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [settings, setSettings] = useState({
    logo: '',
    siteName: '',
    siteSubtitle: '',
    heroTitle: '',
    heroSubtitle: '',
    heroDescription: '',
    heroImage: '',
    phone: '',
    email: '',
    address: '',
    workSchedule: '',
    facebook: '',
    instagram: '',
    telegram: '',
    vk: '',
    aboutTitle: '',
    aboutDescription: '',
    metaDescription: '',
    metaKeywords: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Ошибка загрузки настроек:', error);
    }
  };

  const handleChange = (e) => {
    setSettings({
      ...settings,
      [e.target.name]: e.target.value,
    });
  };

  const handleLogoUpload = (url) => {
    setSettings({
      ...settings,
      logo: url,
    });
  };

  const handleHeroImageUpload = (url) => {
    setSettings({
      ...settings,
      heroImage: url,
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.put('/settings', settings);
      setSnackbar({
        open: true,
        message: 'Настройки успешно сохранены!',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Ошибка при сохранении настроек',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">Настройки сайта</Typography>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Сохранение...' : 'Сохранить все'}
        </Button>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="Логотип и брендинг" />
          <Tab label="Главный экран" />
          <Tab label="Контакты" />
          <Tab label="Соцсети" />
          <Tab label="О центре" />
          <Tab label="SEO" />
        </Tabs>
      </Paper>

      {/* Логотип и брендинг */}
      <TabPanel value={activeTab} index={0}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Логотип
          </Typography>
          <ImageUploader
            currentImage={settings.logo}
            onUploadSuccess={handleLogoUpload}
          />
          
          <Divider sx={{ my: 3 }} />
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Название сайта"
                name="siteName"
                value={settings.siteName}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Подзаголовок сайта"
                name="siteSubtitle"
                value={settings.siteSubtitle}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </Paper>
      </TabPanel>

      {/* Главный экран */}
      <TabPanel value={activeTab} index={1}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Hero секция (главный экран)
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Заголовок"
                name="heroTitle"
                value={settings.heroTitle}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Подзаголовок"
                name="heroSubtitle"
                value={settings.heroSubtitle}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Описание"
                name="heroDescription"
                value={settings.heroDescription}
                onChange={handleChange}
                multiline
                rows={4}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Изображение для главного экрана
              </Typography>
              <ImageUploader
                currentImage={settings.heroImage}
                onUploadSuccess={handleHeroImageUpload}
              />
            </Grid>
          </Grid>
        </Paper>
      </TabPanel>

      {/* Контакты */}
      <TabPanel value={activeTab} index={2}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Контактная информация
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Телефон"
                name="phone"
                value={settings.phone}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={settings.email}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Адрес"
                name="address"
                value={settings.address}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="График работы"
                name="workSchedule"
                value={settings.workSchedule}
                onChange={handleChange}
                multiline
                rows={4}
                helperText="Каждая строка - отдельный день"
              />
            </Grid>
          </Grid>
        </Paper>
      </TabPanel>

      {/* Соцсети */}
      <TabPanel value={activeTab} index={3}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Социальные сети
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Facebook (URL)"
                name="facebook"
                value={settings.facebook}
                onChange={handleChange}
                placeholder="https://facebook.com/..."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Instagram (URL)"
                name="instagram"
                value={settings.instagram}
                onChange={handleChange}
                placeholder="https://instagram.com/..."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Telegram (URL)"
                name="telegram"
                value={settings.telegram}
                onChange={handleChange}
                placeholder="https://t.me/..."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="VK (URL)"
                name="vk"
                value={settings.vk}
                onChange={handleChange}
                placeholder="https://vk.com/..."
              />
            </Grid>
          </Grid>
        </Paper>
      </TabPanel>

      {/* О центре */}
      <TabPanel value={activeTab} index={4}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Секция "О центре"
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Заголовок секции"
                name="aboutTitle"
                value={settings.aboutTitle}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Описание"
                name="aboutDescription"
                value={settings.aboutDescription}
                onChange={handleChange}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </Paper>
      </TabPanel>

      {/* SEO */}
      <TabPanel value={activeTab} index={5}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            SEO настройки
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Мета-описание (Description)"
                name="metaDescription"
                value={settings.metaDescription}
                onChange={handleChange}
                multiline
                rows={3}
                helperText="Краткое описание сайта для поисковых систем (до 160 символов)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Ключевые слова (Keywords)"
                name="metaKeywords"
                value={settings.metaKeywords}
                onChange={handleChange}
                helperText="Ключевые слова через запятую"
              />
            </Grid>
          </Grid>
        </Paper>
      </TabPanel>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsManager;
